import { getPriorityInfo } from '@/utils/taskUtils';

import type { MoveLocationDetail } from '@/service/types/movetasktypes';
import type { TaskDetail, TaskType } from '@/types';

// ─────────────────────────────────────────────────────────────────
// OIC → TaskDetail mapper
// ─────────────────────────────────────────────────────────────────

// OIC returns status as string name; UI uses numeric codes (Xamarin convention)
const STATUS_MAP: Record<string, number> = {
  Released: 20,
  Assigned: 30,
  'In Progress': 40,
  Completed: 60,
};

export interface OICTaskDetail {
  Task_id: string;
  Task_type: string;
  Status: string;
  Priority: string;
  Trailer_name: string;
  Trailer_id: string;
  Carrier_company_name: string;
  Carrier_name: string;
  Move_src_type: string;
  Move_src_zone: string;
  Move_src_name: string;
  Move_dst_type: string;
  Move_dst_zone: string;
  Move_dst_name: string;
  Audit_locn_type: string;
  Audit_locn_zone: string;
  Audit_locn_name: string;
  Created_datetime: string;
  Last_updated_datetime: string;
  Task_start_datetime: string;
  Task_end_datetime: string;
  Task_comments: string;
  Trailer_comments: string;
  Assigned_user_id: string;
  Exception_code: string;
  Elapsed_time: string;
  Alt_trailer_name: string;
  Alt_carrier: string;
  Alt_dst_zone: string;
  Alt_dst_name: string;
}

export function mapOICTask(raw: OICTaskDetail): TaskDetail {
  return {
    TaskId: raw.Task_id,
    TaskType: raw.Task_type as TaskType,
    Status: STATUS_MAP[raw.Status] ?? 0,
    Priority: Number(raw.Priority) || 0,
    PriorityName: getPriorityInfo(Number(raw.Priority) || 0).name,
    TrailerName: raw.Trailer_name,
    TrailerId: raw.Trailer_id,
    CarrierCompanyName: raw.Carrier_company_name,
    CarrierName: raw.Carrier_name,
    MoveSourceType: raw.Move_src_type,
    MoveSourceZone: raw.Move_src_zone,
    MoveSourceName: raw.Move_src_name,
    MoveDestinationType: raw.Move_dst_type,
    MoveDestinationZone: raw.Move_dst_zone,
    MoveDestinationName: raw.Move_dst_name,
    AuditLocationType: raw.Audit_locn_type,
    AuditLocationZone: raw.Audit_locn_zone,
    AuditLocationName: raw.Audit_locn_name,
    CreatedDatetime: raw.Created_datetime,
    LastUpdatedDatetime: raw.Last_updated_datetime,
    TaskStartDateTime: raw.Task_start_datetime,
    TaskEndDateTime: raw.Task_end_datetime,
    TaskDescription: '',
    TaskComments: raw.Task_comments,
    TrailerComments: raw.Trailer_comments,
    AssignedUserId: raw.Assigned_user_id,
    AssignedUserName: '',
    ExceptionCode: raw.Exception_code,
    ExceptionText: '',
    ElapsedTime: Number(raw.Elapsed_time) || 0,
    AlternateTrailerName: raw.Alt_trailer_name,
    AlternateCarrier: raw.Alt_carrier,
    AlternateDestinationZone: raw.Alt_dst_zone,
    AlternateDestinationName: raw.Alt_dst_name,
  };
}

// ─────────────────────────────────────────────────────────────────
// OIC → MoveLocationDetail mapper
// ─────────────────────────────────────────────────────────────────

export interface OICLocationDetail {
  Loc_Name: string;
  Loc_Zone: string;
  Loc_Type: string;
  Loc_Status: string;
  Trailer_Name: string;
  Carrier_Scac: string;
  Carrier_Name: string;
  Last_Audit_Dttm: string;
}

export function mapOICLocation(raw: OICLocationDetail): MoveLocationDetail {
  return {
    LocationName: raw.Loc_Name,
    LocationZone: raw.Loc_Zone,
    LocationType: raw.Loc_Type,
    LocStatus: raw.Loc_Status,
    TrailerName: raw.Trailer_Name,
    CarrierName: raw.Carrier_Name,
    CarrierCode: raw.Carrier_Scac,
    LastAuditTime: raw.Last_Audit_Dttm,
  };
}
